import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Filter } from "lucide-react";
import { api } from "@/lib/api";
import {
  VLKPageHeader, VLKCard, VLKSelect, VLKInput, VLKButton, VLKRiskMeter, VLKAuditRow,
} from "@/components/vlk";
import { IDS } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuditLog() {
  const [action, setAction] = useState("");
  const [role, setRole] = useState("");
  const [risk, setRisk] = useState("");

  const { data } = useQuery({
    queryKey: ["audit", action, role, risk],
    queryFn: () => api.get("/audit", { params: { action: action || undefined, role: role || undefined, risk_level: risk || undefined, limit: 200 } }).then(r => r.data),
  });

  const exportUrl = `${BACKEND_URL}/api/audit/export`;

  return (
    <div>
      <VLKPageHeader
        title="Audit ledger"
        description="Tamper-evident, integrity-hashed audit trail. Cryptographically chained. Write-once at the application layer."
        actions={
          <a href={exportUrl} data-testid={IDS.auditExport}>
            <VLKButton variant="outline" size="md">
              <Download className="h-4 w-4" />Export CSV
            </VLKButton>
          </a>
        }
      />

      <VLKCard className="mb-4">
        <div className="p-3 flex flex-col md:flex-row gap-2 border-b border-border">
          <VLKInput placeholder="Action (regex)…" value={action} onChange={(e) => setAction(e.target.value)} className="max-w-xs" />
          <VLKSelect value={role} onChange={(e) => setRole(e.target.value)} className="max-w-[200px]">
            <option value="">All roles</option>
            <option value="system_admin">system_admin</option>
            <option value="compliance_officer">compliance_officer</option>
            <option value="security_officer">security_officer</option>
            <option value="identity_officer">identity_officer</option>
            <option value="auditor">auditor</option>
            <option value="institution_admin">institution_admin</option>
          </VLKSelect>
          <VLKSelect value={risk} onChange={(e) => setRisk(e.target.value)} className="max-w-[160px]">
            <option value="">All risk</option>
            <option value="low">low</option><option value="medium">medium</option>
            <option value="high">high</option><option value="critical">critical</option>
          </VLKSelect>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 border-b border-border">
              <tr>
                {["Timestamp","Actor","Role","Action","Target","Risk","Outcome"].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((row) => (
                <tr key={row.ledger_id} data-testid={IDS.auditRow(row.ledger_id)} className="border-b border-border hover:bg-secondary/50 font-mono text-xs">
                  <td className="px-4 py-2 whitespace-nowrap">{new Date(row.timestamp).toLocaleString()}</td>
                  <td className="px-4 py-2">{row.actor_email}</td>
                  <td className="px-4 py-2 text-muted-foreground">{row.role}</td>
                  <td className="px-4 py-2 font-medium">{row.action}</td>
                  <td className="px-4 py-2 text-muted-foreground">{row.target_id || "—"}</td>
                  <td className="px-4 py-2"><VLKRiskMeter level={row.risk_level} /></td>
                  <td className="px-4 py-2">
                    <span className={row.outcome === "SUCCESS" ? "text-success" : "text-destructive"}>{row.outcome}</span>
                  </td>
                </tr>
              ))}
              {(data?.items || []).length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground text-sm">No audit events match filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </VLKCard>
    </div>
  );
}
